import React from 'react'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import useIsMobile from '../hooks/useIsMobile'

const spotlightGradientCss = css`
  position: relative;
  max-width: 100vw;
  --side-scrim-color: black;
  --bottom-scrim-color: black;
  &[is-mobile] {
    --side-scrim-color: transparent;
    --bottom-scrim-color: transparent;
  }

  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 50vh;
    z-index: -1;
  }

  --scrim-multiplier: 1;
  --side-scrim-default: 15%;
  --bottom-scrim-default: 65%;
  --side-scrim-amount: calc(var(--side-scrim-default) * var(--scrim-multiplier));
  --bottom-scrim-amount: calc(var(--bottom-scrim-default) * var(--scrim-multiplier));

  &[gradient-size='sm']::before {
    height: 50vh;
  }
  &[gradient-size='md']::before {
    height: 75vh;
  }
  &[gradient-size='lg']::before {
    height: 120vh;
    background-size: 200px 200px;
    --scrim-multiplier: 0.1;
    --side-scrim-amount: calc(var(--side-scrim-default) * var(--scrim-multiplier));
    --bottom-scrim-amount: calc(var(--bottom-scrim-default) * var(--scrim-multiplier));
  }

  &[is-mobile]::before {
    opacity: 0.9;
  }

  &[flavor='blueberry']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent var(--side-scrim-amount)),
      /*        */ linear-gradient(360deg, black 0%, transparent var(--bottom-scrim-default)),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent var(--side-scrim-amount)),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #00a7e7dd 25%, #d83b95 65%),
      /*        */ linear-gradient(95deg, #00c7f7dd 25%, #d83b95 70%);
  }
  &[flavor='apple']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #00b1cfbb 25%, #7fc350bb 65%);
  }
  &[flavor='mystery']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #00a7e7bb 25%, #00b1cfbb 65%);
  }
  &[flavor='galaxy']::before {
    background: linear-gradient(90deg, transparent 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, transparent 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #004c7a 25%, #7503c2 65%);
  }
  &[flavor='familysearch']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #355800 25%, #86ba41 65%);
  }
  &[flavor='ancestry']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #202122 25%, #353637 65%);
  }
  &[flavor='myheritage']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #892f00 25%, #e46e31 65%);
  }
  &[flavor='frost']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #252048 25%, #6c93ca 65%);
  }
  &[flavor='mango']::before {
    background: linear-gradient(90deg, var(--side-scrim-color) 0%, transparent 15%),
      /*        */ linear-gradient(360deg, black 0%, transparent 65%),
      /*        */ linear-gradient(270deg, var(--side-scrim-color) 0%, transparent 25%),
      /*        */ radial-gradient(ellipse at top, transparent 20%, var(--bottom-scrim-color) 75%),
      /*        */ linear-gradient(100deg, #d83b95cc 25%, #ffcc5ccc 65%);
  }
`

export default function SpotlightGradient({ flavor = 'blueberry', size = 'sm' }) {
  const isMobile = useIsMobile()
  return <div css={spotlightGradientCss} gradient-size={size} flavor={flavor} is-mobile={boolAttr(isMobile)} />
}
